/**
 * Copyright (C) Firstlight Media. All rights reserved.
 * All information contained herein is and remains the property of Firstlight Media.
 * The intellectual and technical concepts contained herein are proprietary
 * to Firstlight Media. Dissemination of this information or reproduction
 * of this material is strictly forbidden unless prior written permission
 * is obtained from Firstlight Media.
 */

/**
 * Variant track types.
 */
export enum TrackVariantType {
  VARIANT = 'variant',
  TEXT = 'text',
  IMAGE = 'image',
}

/**
 * Represents a variant track.
 */
export interface TrackVariantInfo {
  /**
   * The unique ID of the track.
   */
  id?: string;

  /**
   * The type of track.
   */
  type?: TrackVariantType;

  /**
   * The language of the track, or 'und' if not given. This is the
   * exact value provided in the manifest; it may need to be normalized.
   */
  language?: string;

  /**
   * The MIME type of the content provided in the manifest.
   */
  mimeType?: string;

  /**
   * The bandwidth required to play the track, in bits/sec.
   */
  bitrate?: number;

  /**
   * The count of the audio track channels.
   */
  channelsCount?: number;

  /**
   * The audio/video codecs string provided in the manifest, if present.
   */
  codecs?: string;

  /**
   * Specifies the maximum sampling rate of the content.
   */
  sampleRate?: number;

  /**
   * The video width provided in the manifest, if present.
   */
  width?: number;

  /**
   * The video height provided in the manifest, if present.
   */
  height?: number;

  /**
   * The video framerate provided in the manifest, if present.
   */
  frameRate?: number;

  /**
   * True indicates that the content has spatial audio. This flag
   * is based on signals from the manifest.
   */
  spatialAudio?: boolean;

  /**
   * The video HDR provided in the manifest, if present.
   */
  hdr?: string;

  /**
   * The video pixel aspect ratio provided in the manifest, if present.
   */
  pixelAspectRatio?: string;

  /**
   * The roles of the audio in the track, e.g. 'main' or 'commentary'.
   * Will be null for text tracks or variant tracks without audio.
   */
  audioRoles?: string[];

  /***
   * Bandwidth requirement of the track in bits per second.
   */
  bandwidth?: number;
}
